import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    (async () => {
      try {
        await api.post("/auth/session", { session_id: sessionId });
        const { data } = await api.get("/auth/me");
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      } catch (e) {
        navigate("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0D12]">
      <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
    </div>
  );
}

function DemoLanding() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const token = new URLSearchParams(window.location.search).get("token");
    (async () => {
      try {
        await api.post("/auth/demo", { token });
        const { data } = await api.get("/auth/me");
        setUser(data);
        navigate("/", { replace: true });
      } catch (e) {
        setError("Este acesso demo é inválido ou já expirou. Peça um novo link ao administrador.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0D12] px-4">
      {error ? (
        <div className="max-w-md w-full bg-[#0F1520] border border-red-500/30 rounded-3xl p-8 text-center">
          <p className="text-slate-200 mb-4">{error}</p>
          <a href="/login" className="text-emerald-400 hover:text-emerald-300 text-sm">Ir para o login</a>
        </div>
      ) : (
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      )}
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Process the Emergent OAuth callback FIRST (synchronously during render) to avoid race conditions
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/demo" element={<DemoLanding />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-center" richColors theme="dark" />
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
